import { initData } from './telegram'
import { authMode, sessionToken, expireSession } from './auth'
import { isStandalone } from './install'
import type { Lang } from '../data/cards'
import type { SpreadId } from '../data/spreads'
import type { FormatId } from '../data/formats'
import type { AskLine, DrawnCard, Profile, ReadingRecord } from './storage'
import type { Place } from '../../../shared/places.ts'

const BASE = import.meta.env.VITE_API_BASE ?? '/api'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
  }
}

/**
 * Заголовки авторизации под текущий способ входа: в Telegram — подпись
 * initData, в PWA — токен сессии. Отправлять оба безвредно (сервер смотрит
 * initData первым, а в браузере он пуст), но лишнего не шлём.
 */
function authHeaders(): Record<string, string> {
  if (authMode === 'telegram') return { 'X-Telegram-Init-Data': initData() }
  const token = sessionToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Откуда мы сейчас: Telegram, установленное на экран приложение или браузер.
 *
 * Считается на каждый запрос, а не один раз при загрузке: человек ставит
 * приложение на экран прямо в сеансе, и с этого момента он уже «pwa». Стоит
 * это одну проверку `matchMedia`.
 */
function platform(): string {
  if (authMode !== 'pwa') return 'telegram'
  return isStandalone() ? 'pwa' : 'web'
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Sirr-Platform': platform(),
      ...authHeaders(),
      ...init?.headers,
    },
  })

  // Сессия PWA истекла или отозвана — чистим её и просим экран входа.
  if (res.status === 401 && authMode === 'pwa') expireSession()

  if (!res.ok) {
    let code: string | undefined
    let message = `HTTP ${res.status}`
    try {
      const body = (await res.json()) as { error?: string; code?: string }
      message = body.error ?? message
      code = body.code
    } catch {
      /* тело не JSON — оставляем статус */
    }
    throw new ApiError(message, res.status, code)
  }
  return (await res.json()) as T
}

export interface InterpretRequest {
  spread: SpreadId
  cards: DrawnCard[]
  lang: Lang
  /** Месяц (0–11), с которого начинается Колесо года. */
  startMonth?: number
  goals?: string[]
  zodiac?: string
  question?: string
  /** Тема чтения, выбранная перед раскладом. */
  theme?: string
}

export interface InterpretResponse {
  id: string
  /** Момент, с которого расклад можно показать; 0 — сразу. */
  readyAt?: number
  overall: string
  perCard: string[]
  /**
   * Карты сохранённого расклада — приходят вместе с `repeated`. Свои могут
   * не совпасть: правило «один расклад в день» держит сервер, и если история
   * на устройстве ещё не подтянулась, клиент нарисует другие карты.
   */
  cards?: DrawnCard[]
  /** Расклад уже был сделан сегодня — отдан сохранённый, без списания. */
  repeated?: boolean
  /** Сколько бесплатных раскладов осталось сегодня. */
  remaining: number
  /** Баланс Stars после списания за платный расклад. */
  stars?: number
}

export interface FortuneRequest {
  format: FormatId
  lang: Lang
  /** Снимок data-ссылкой; только для гаданий по фото. */
  image?: string
  /** Сон целиком или желание, загаданное джинну. */
  text?: string
  /** Тема чтения, выбранная перед гаданием. */
  theme?: string
  goals?: string[]
}

export interface FortuneResponse {
  id: string
  ts: number
  format: FormatId
  /** Название сна — заголовок записи в дневнике; у остальных пусто. */
  title: string
  overall: string
  sections: { label: string; text: string }[]
  chat: { who: 'p' | 'u'; text: string }[]
  /** Сколько раз ещё можно обратиться к персонажу; 0 — разговор окончен. */
  turnsLeft: number
  /** Баланс после списания. */
  stars?: number
}

/** Запись из дневника снов или истории гаданий. */
export interface FortuneRecord extends FortuneResponse {
  lang: Lang
  /** Что человек прислал — текст сна или желание. Для фото пусто. */
  input: string
}

export interface AstroRequest {
  kind: 'compat' | 'natal' | 'daily'
  lang: Lang
  a: { year: number; month: number; day: number; hour?: number | null; minute?: number | null; place?: Place | null }
  b?: { year: number; month: number; day: number }
  goals?: string[]
}

export interface ProfileResponse {
  profile: Partial<Profile> & { history?: ReadingRecord[] }
  remaining: number
  limit: number
  /**
   * Награда за рекламу: сколько звёзд даёт ролик и сколько роликов осталось
   * сегодня. Оба числа настраиваются на сервере, поэтому приходят оттуда —
   * зашитые в клиенте обещали бы не то, что начислится. У подписчика остаток
   * нулевой: рекламы он не видит вовсе.
   */
  ads?: { stars: number; left: number; install?: number; notify?: number; login?: number }
  /** Сколько платных раскладов и гаданий сегодня покрыто подпиской. */
  included?: number
  /** До какого момента действует льготная цена подписки; 0 — уже нет. */
  introUntil?: number
  /**
   * Человек ещё не назвался — сессия анонимная.
   *
   * Отвечает сервер: клиент про свой токен знает лишь, что тот есть.
   * На этом поле держится всё предложение войти — и то, что тем, кто уже
   * вошёл, его не показывают.
   */
  anon?: boolean
  /** Осталось ли первое даровое гадание. Считает и занимает его сервер. */
  freeFortune?: boolean
}

export const api = {
  /** Загружает готовую картинку карточки; возвращает публичный адрес. */
  async uploadShareCard(image: Blob, lang: string): Promise<{ id: string; url: string; invite: string }> {
    const res = await fetch(`${BASE}/share?lang=${encodeURIComponent(lang)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'image/jpeg', ...authHeaders() },
      body: image,
    })
    if (res.status === 401 && authMode === 'pwa') expireSession()
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
    return (await res.json()) as { id: string; url: string; invite: string }
  },

  /** Готовит сообщение с карточкой для системного «поделиться». */
  /**
   * Удаляет учётную запись со всеми данными. Подтверждение уходит полем, а не
   * одним лишь методом: цена случайного запроса здесь — всё, что человек
   * накопил, без возможности вернуть.
   */
  deleteAccount() {
    return request<{ ok: boolean }>('/account/delete', {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    })
  },

  /** Аналитика: пачка событий. Ответ не важен, важно не мешать работе. */
  sendEvents(events: { name: string; ts: number; props?: unknown }[]) {
    return request<{ ok: boolean }>('/events', {
      method: 'POST',
      body: JSON.stringify({ events }),
    })
  },

  shareMessage(body: { id: string; title: string; caption: string; lang: string }) {
    return request<{ preparedMessageId: string }>('/share/message', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  /** Поиск места рождения: координаты и часовой пояс. */
  places(query: string, lang: Lang, signal?: AbortSignal) {
    return request<{ places: Place[]; degraded: boolean }>(
      `/places?q=${encodeURIComponent(query)}&lang=${lang}`,
      { signal },
    )
  },

  /**
   * Толкование натальной карты или совместимости. Сервер считает положения
   * сам из тех же данных рождения — клиент не присылает готовые числа.
   */
  astro(body: AstroRequest, signal?: AbortSignal) {
    return request<{ text: string; remaining: number }>('/astro', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    })
  },

  /** AI-толкование расклада. Здесь же бэкенд списывает лимит free-тира. */
  interpret(body: InterpretRequest, signal?: AbortSignal) {
    return request<InterpretResponse>('/interpret', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    })
  },

  /**
   * Вопрос по уже сделанному раскладу. Списание и счёт вопросов — на сервере;
   * ответ приносит переписку целиком и сколько вопросов осталось.
   */
  askReading(id: string, question: string, startMonth?: number) {
    return request<{ chat: AskLine[]; turnsLeft: number; balance?: number }>(
      `/reading/${encodeURIComponent(id)}/ask`,
      { method: 'POST', body: JSON.stringify({ question, startMonth }) },
    )
  },

  /**
   * Гадание без карт. Списание звёзд — на сервере, до обращения к модели;
   * при сбое он же делает возврат.
   */
  fortune(body: FortuneRequest, signal?: AbortSignal) {
    return request<FortuneResponse>('/fortune', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    })
  },

  /**
   * Очередная реплика в разговоре с персонажем. Звёзды не списываются —
   * за разговор заплачено при его начале; границу держит счётчик ходов.
   */
  reply(id: string, body: { message: string; lang: Lang; theme?: string; goals?: string[] }, signal?: AbortSignal) {
    return request<{ chat: { who: 'p' | 'u'; text: string }[]; turnsLeft: number }>(
      `/fortune/${encodeURIComponent(id)}/reply`,
      { method: 'POST', body: JSON.stringify(body), signal },
    )
  },

  /** Дневник снов (`format: 'dream'`) или вся история гаданий. */
  fortunes(format?: FormatId) {
    return request<{ fortunes: FortuneRecord[] }>(
      format ? `/fortunes?format=${format}` : '/fortunes',
    )
  },

  /** Профиль с сервера — источник правды для лимитов и Stars. */
  me() {
    return request<ProfileResponse>('/me')
  },

  /**
   * Сообщает, что приложение запущено из установленного вида, и забирает
   * награду. Зовётся при КАЖДОМ таком запуске — защита от повторов в самом
   * запросе на сервере, и это правильнее, чем помнить на клиенте: локальную
   * память чистят, а база помнит.
   */
  pwaInstalled() {
    return request<{ granted: number; stars: number }>('/pwa/installed', { method: 'POST' })
  },

  /**
   * Двусторонняя синхронизация. Расклады объединяются по id, скалярные поля
   * профиля перезаписываются — поэтому `profile` передаётся только тогда,
   * когда локальное состояние уже основано на серверном.
   */
  sync(body: { profile?: Partial<Profile>; history: ReadingRecord[] }) {
    return request<ProfileResponse>('/sync', {
      method: 'POST',
      // Часовой пояс добавляем здесь, а не в профиле: это свойство устройства,
      // а не человека, и хранить его в профиле значило бы возить между
      // устройствами чужое значение. Нужен он одному — рассылке, чтобы
      // уведомление приходило в вечер человека, а не в наш.
      body: JSON.stringify({ ...body, tz: timeZone() }),
    })
  },

  /**
   * Создаёт оплату пакета. В Telegram возвращает инвойс Stars
   * (`kind: 'invoice'` → openInvoice), в PWA — ссылку на страницу оплаты lava
   * (`kind: 'url'`).
   */
  invoice(pkg: number, lang: Lang) {
    return request<{ kind: 'invoice' | 'url'; value: string }>('/invoice', {
      method: 'POST',
      body: JSON.stringify({ pkg, lang }),
    })
  },

  /** Публичный ключ VAPID для подписки на Web Push. */
  pushKey() {
    return request<{ key: string }>('/push/key')
  },

  /** Сохраняет подписку Web Push на сервере. */
  pushSubscribe(sub: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    // `bonus` приходит, когда сервер начислил награду за уведомления: он
    // делает это здесь же, увидев подписку, а не по отдельной просьбе клиента.
    return request<{ ok: boolean; bonus?: number; stars?: number }>('/push/subscribe', { method: 'POST', body: JSON.stringify(sub) })
  },

  pushUnsubscribe(endpoint: string) {
    return request<{ ok: boolean }>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) })
  },

  /** Согласие на уведомления — отдельно от синхронизации профиля. */
  setNotify(on: boolean) {
    return request<{ ok: boolean }>('/notify', { method: 'POST', body: JSON.stringify({ on }) })
  },

  /** Реферальная сводка: код, приведённые друзья, прибавка к лимиту. */
  /** Готов ли расклад и, если готов, его текст. Нужна при отложенной выдаче. */
  reading(id: string) {
    return request<{ id: string; readyAt: number; overall: string; perCard: string[] }>(
      `/reading/${encodeURIComponent(id)}`,
    )
  },

  /** Открыть расклад досрочно. Пока доступно только подписчику. */
  revealReading(id: string) {
    return request<{ id: string; readyAt: number; overall: string; perCard: string[] }>(
      `/reading/${encodeURIComponent(id)}/reveal`,
      { method: 'POST' },
    )
  },

  /** Условия подписки и её состояние. Числа — с сервера, они настраиваются. */
  subscription() {
    return request<{
      stars: number
      perDay: number
      dailyStars: number
      /** Главное обещание подписки: сколько платного в день входит в неё. */
      includedPerDay: number
      includedLeft: number
      /**
       * Планы с ценами НА СЕЙЧАС. Скидку считает сервер: посчитай её здесь —
       * и витрина однажды пообещает не то, что выставит счёт.
       */
      plans: {
        id: string
        days: number
        price: number
        full: number
        best: boolean
        available: boolean
      }[]
      introUntil: number
      subscribed: boolean
      subUntil: number
      available: boolean
    }>('/subscription')
  },

  /** Счёт на подписку. Пока только Telegram Stars — сервер это и проверяет. */
  /**
   * Оформление подписки. `granted` — пробный период выдан сразу, без оплаты;
   * `invoice` — надо открыть счёт. Что именно, решает сервер по плану: цену и
   * вид счёта клиент не выбирает.
   */
  subscribe(plan: string) {
    return request<{ kind: 'invoice'; value: string } | { kind: 'granted'; subUntil: number }>(
      '/subscribe',
      { method: 'POST', body: JSON.stringify({ plan }) },
    )
  },

  referral() {
    return request<{
      code: string
      invited: number
      earned: number
      cap: number
      perFriend: number
      welcomeStars: number
    }>('/referral')
  },

  /** Привязка к пригласившему по коду (для нового пользователя). */
  claimReferral(code: string) {
    return request<{ result: string }>('/referral/claim', { method: 'POST', body: JSON.stringify({ code }) })
  },
}

/** Имя часовой зоны устройства. Пустая строка — если браузер её не сообщает. */
function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  } catch {
    return ''
  }
}
